import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { useStoreInfo } from "@/context/StoreInfoContext";

export default function Contact() {
  const store = useStoreInfo();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent!", { description: "We'll be in touch shortly." });
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Something went wrong");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8" data-testid="contact-page">
      <div className="grid gap-12 lg:grid-cols-2">
        <div>
          <span className="chip bg-brand-teal/15 text-brand-teal">Say hi</span>
          <h1 className="mt-3 font-display text-4xl font-bold text-brand-navy sm:text-5xl">
            Get in touch
          </h1>
          <p className="mt-4 text-lg text-slate-600">
            Questions on stock, tournament formats, ordering, or something we should carry?
            Drop us a note — we usually reply within 1 business day.
          </p>

          <ul className="mt-8 space-y-4">
            {store.address_full && <Info icon={<MapPin />} label="Visit us" value={store.address_full} />}
            {store.phone && <Info icon={<Phone />} label="Call" value={store.phone} />}
            {store.email && <Info icon={<Mail />} label="Email" value={store.email} />}
            {store.hours && <Info icon={<Clock />} label="Hours" value={store.hours} />}
          </ul>
        </div>

        <form onSubmit={submit} className="rounded-3xl border border-slate-200 bg-white p-8" data-testid="contact-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name*">
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={inputCls}
                data-testid="contact-name"
              />
            </Field>
            <Field label="Email*">
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputCls}
                data-testid="contact-email"
              />
            </Field>
          </div>
          <Field label="Subject*" className="mt-4">
            <input
              required
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className={inputCls}
              data-testid="contact-subject"
            />
          </Field>
          <Field label="Message*" className="mt-4">
            <textarea
              required
              rows={6}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className={`${inputCls} h-auto py-3`}
              data-testid="contact-message"
            />
          </Field>
          <button
            type="submit"
            disabled={sending}
            className="btn-primary mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold uppercase tracking-wider disabled:bg-slate-300"
            data-testid="contact-submit"
          >
            <Send size={16} /> {sending ? "Sending…" : "Send message"}
          </button>
        </form>
      </div>
    </div>
  );
}

const inputCls =
  "h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/30";

function Field({ label, children, className }) {
  return (
    <label className={`block text-xs font-semibold text-slate-600 ${className || ""}`}>
      <span className="mb-1 block uppercase tracking-wider">{label}</span>
      {children}
    </label>
  );
}

function Info({ icon, label, value }) {
  return (
    <li className="flex items-start gap-4">
      <div className="mt-0.5 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand-teal/10 text-brand-teal">
        {icon}
      </div>
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-slate-500">{label}</p>
        <p className="mt-0.5 font-semibold text-brand-navy">{value}</p>
      </div>
    </li>
  );
}
